import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Loader2, Plus, Swords, Trash2, X } from "lucide-react";
import { VariavelWiredSelect } from "../../VariavelWiredSelect";
import {
  WIRED_LOJA_OPERADORES,
  WiredConexao,
  WiredLojaRequisito,
  WiredMissao,
  WiredMissaoObjetivo,
  WiredMissaoPrazoTipo,
  WiredMissaoRecompensa,
  WiredNivel,
  wiredService,
} from "../../../services/wired";

interface WiredMissaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  missao: WiredMissao | null;
  conexoes: WiredConexao[];
  onSuccess?: () => void;
}

const NOME_VARIAVEL_REGEX = /^[\w.-]{1,128}$/;
const INTEIRO_REGEX = /^-?\d{1,18}$/;
const MAX_ITENS = 10;
const MAX_OBJETIVOS = 30;

const paraInputData = (iso: string | null | undefined) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export function WiredMissaoModal({ isOpen, onClose, missao, conexoes, onSuccess }: WiredMissaoModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [idConexao, setIdConexao] = useState("");
  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [imagem, setImagem] = useState("");
  const [imagemQuebrada, setImagemQuebrada] = useState(false);
  const [prazoTipo, setPrazoTipo] = useState<WiredMissaoPrazoTipo>("nenhum");
  const [prazoValor, setPrazoValor] = useState("24");
  const [prazoUnidade, setPrazoUnidade] = useState<"horas" | "dias">("horas");
  const [prazoAte, setPrazoAte] = useState("");
  const [repetivel, setRepetivel] = useState(false);
  const [cooldown, setCooldown] = useState("");
  const [sorteio, setSorteio] = useState("");
  const [limite, setLimite] = useState("");
  const [objetivos, setObjetivos] = useState<WiredMissaoObjetivo[]>([]);
  const [requisitos, setRequisitos] = useState<WiredLojaRequisito[]>([]);
  const [recompensas, setRecompensas] = useState<WiredMissaoRecompensa[]>([]);
  const [ativo, setAtivo] = useState(true);
  const [error, setError] = useState("");
  const [niveis, setNiveis] = useState<WiredNivel[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    wiredService
      .listarNiveis()
      .then(setNiveis)
      .catch(() => setNiveis([]));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const horas = missao?.prazo_horas ?? null;
    setIdConexao(missao?.id_conexao ?? conexoes[0]?.id ?? "");
    setNome(missao?.nome ?? "");
    setDescricao(missao?.descricao ?? "");
    setImagem(missao?.imagem_url ?? "");
    setPrazoTipo(missao?.prazo_tipo ?? "nenhum");
    if (horas && horas % 24 === 0) {
      setPrazoUnidade("dias");
      setPrazoValor(String(horas / 24));
    } else {
      setPrazoUnidade("horas");
      setPrazoValor(horas ? String(horas) : "24");
    }
    setPrazoAte(paraInputData(missao?.prazo_ate));
    setRepetivel(missao?.repetivel ?? false);
    setCooldown(missao?.cooldown_horas != null ? String(missao.cooldown_horas) : "");
    setSorteio(missao?.qtd_sorteio != null ? String(missao.qtd_sorteio) : "");
    setLimite(missao?.limite_simultaneas != null ? String(missao.limite_simultaneas) : "");
    setObjetivos(missao?.objetivos ?? []);
    setRequisitos(missao?.requisitos ?? []);
    setRecompensas(missao?.recompensas ?? []);
    setAtivo(missao?.ativo ?? true);
    setError("");
    setImagemQuebrada(false);
  }, [isOpen, missao, conexoes]);

  const niveisXp = niveis.filter((n) => n.id_conexao === idConexao).map((n) => n.nome_variavel_xp);
  const conexaoAtiva = conexoes.find((c) => c.id === idConexao);
  const semChave = recompensas.some((r) => {
    const c = conexoes.find((x) => x.id === (r.id_conexao ?? idConexao));
    return !!c && !c.possui_chave_escrita;
  });

  const horasDoPrazo = () => Number(prazoValor) * (prazoUnidade === "dias" ? 24 : 1);

  const validar = (): string => {
    if (!idConexao) return "Selecione uma conexão.";
    if (!nome.trim() || nome.trim().length > 80) return "Nome é obrigatório (máximo 80 caracteres).";
    if (descricao.trim().length > 1000) return "Descrição muito longa (máximo 1000 caracteres).";
    if (imagem.trim() && !/^https?:\/\/\S+$/i.test(imagem.trim())) return "O link da imagem deve começar com http ou https.";
    if (prazoTipo === "relativo") {
      const h = horasDoPrazo();
      if (!Number.isInteger(h) || h < 1 || h > 24 * 365) return "Informe um prazo válido (até 365 dias).";
    }
    if (prazoTipo === "fixo") {
      if (!prazoAte) return "Informe a data limite.";
      if (new Date(prazoAte).getTime() <= Date.now()) return "A data limite precisa estar no futuro.";
      if (repetivel) return "Missão com data fixa não pode ser repetível.";
    }
    if (repetivel && cooldown.trim()) {
      const c = Number(cooldown);
      if (!Number.isInteger(c) || c < 1) return "O intervalo deve ser um número de horas maior que 0.";
    }
    if (objetivos.length === 0) return "Cadastre ao menos um objetivo.";
    if (repetivel && objetivos.some((o) => o.modo === "retroativo")) {
      return "Objetivo retroativo não pode ser usado em missão repetível.";
    }
    for (let i = 0; i < objetivos.length; i++) {
      const o = objetivos[i];
      const p = `Objetivo ${i + 1}`;
      if (!o.rotulo.trim() || o.rotulo.trim().length > 80) return `${p}: rótulo é obrigatório (máximo 80 caracteres).`;
      if (!NOME_VARIAVEL_REGEX.test(o.nome_variavel.trim())) return `${p}: variável inválida.`;
      if (!INTEIRO_REGEX.test(o.meta.trim()) || BigInt(o.meta.trim()) <= BigInt(0)) return `${p}: a meta deve ser maior que 0.`;
    }
    if (sorteio.trim()) {
      const s = Number(sorteio);
      if (!Number.isInteger(s) || s < 1) return "A quantidade sorteada deve ser maior que 0.";
      if (s > objetivos.filter((o) => !o.obrigatorio).length) {
        return "A quantidade sorteada não pode exceder o total de objetivos não obrigatórios.";
      }
    }
    if (limite.trim()) {
      const l = Number(limite);
      if (!Number.isInteger(l) || l < 1 || l > 100) return "O limite simultâneo deve ser um inteiro entre 1 e 100.";
    }
    for (let i = 0; i < requisitos.length; i++) {
      const r = requisitos[i];
      const p = `Requisito ${i + 1}`;
      if (!r.rotulo.trim() || r.rotulo.trim().length > 60) return `${p}: rótulo é obrigatório (máximo 60 caracteres).`;
      if (!NOME_VARIAVEL_REGEX.test(r.nome_variavel.trim())) return `${p}: variável inválida.`;
      if (!INTEIRO_REGEX.test(r.valor.trim())) return `${p}: o valor deve ser um número inteiro.`;
    }
    for (let i = 0; i < recompensas.length; i++) {
      const r = recompensas[i];
      const p = `Recompensa ${i + 1}`;
      if (!r.rotulo.trim() || r.rotulo.trim().length > 60) return `${p}: rótulo é obrigatório (máximo 60 caracteres).`;
      if (!NOME_VARIAVEL_REGEX.test(r.nome_variavel.trim())) return `${p}: variável inválida.`;
      if (!INTEIRO_REGEX.test(r.valor.trim()) || BigInt(r.valor.trim()) <= BigInt(0)) return `${p}: o valor deve ser maior que 0.`;
      if (r.teto_valor?.trim() && !INTEIRO_REGEX.test(r.teto_valor.trim())) return `${p}: o teto deve ser um número inteiro.`;
    }
    return "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const mensagem = validar();
    if (mensagem) {
      setError(mensagem);
      return;
    }

    const payload = {
      id_conexao: idConexao,
      nome: nome.trim(),
      descricao: descricao.trim() || null,
      imagem_url: imagem.trim() || null,
      prazo_tipo: prazoTipo,
      prazo_horas: prazoTipo === "relativo" ? horasDoPrazo() : null,
      prazo_ate: prazoTipo === "fixo" ? new Date(prazoAte).toISOString() : null,
      repetivel,
      cooldown_horas: repetivel && cooldown.trim() ? Number(cooldown) : null,
      qtd_sorteio: sorteio.trim() ? Number(sorteio) : null,
      limite_simultaneas: limite.trim() ? Number(limite) : null,
      objetivos: objetivos.map((o) => ({
        id: o.id,
        rotulo: o.rotulo.trim(),
        nome_variavel: o.nome_variavel.trim(),
        meta: o.meta.trim(),
        modo: o.modo,
        obrigatorio: !!o.obrigatorio,
        id_conexao: o.id_conexao && o.id_conexao !== idConexao ? o.id_conexao : null,
      })),      requisitos: requisitos.map((r) => ({
        tipo: niveisXp.includes(r.nome_variavel.trim()) && r.tipo === "nivel" ? ("nivel" as const) : ("variavel" as const),
        rotulo: r.rotulo.trim(),
        nome_variavel: r.nome_variavel.trim(),
        operador: r.operador,
        valor: r.valor.trim(),
      })),
      recompensas: recompensas.map((r) => ({
        rotulo: r.rotulo.trim(),
        nome_variavel: r.nome_variavel.trim(),
        valor: r.valor.trim(),
        teto_valor: r.teto_valor?.trim() ? r.teto_valor.trim() : null,
        id_conexao: r.id_conexao && r.id_conexao !== idConexao ? r.id_conexao : null,
      })),
      ativo,
    };

    try {
      setSubmitting(true);
      if (missao) {
        await wiredService.atualizarMissao(missao.id, payload);
        enqueueSnackbar("Missão atualizada com sucesso!", { variant: "success" });
      } else {
        await wiredService.criarMissao(payload);
        enqueueSnackbar("Missão criada com sucesso!", { variant: "success" });
      }
      onClose();
      if (onSuccess) onSuccess();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || "Erro ao salvar missão", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const alterarObjetivo = (i: number, parcial: Partial<WiredMissaoObjetivo>) => {
    setObjetivos((atual) => atual.map((x, j) => (j === i ? { ...x, ...parcial } : x)));
    setError("");
  };
  const alterarRequisito = (i: number, parcial: Partial<WiredLojaRequisito>) => {
    setRequisitos((atual) => atual.map((x, j) => (j === i ? { ...x, ...parcial } : x)));
    setError("");
  };
  const alterarRecompensa = (i: number, parcial: Partial<WiredMissaoRecompensa>) => {
    setRecompensas((atual) => atual.map((x, j) => (j === i ? { ...x, ...parcial } : x)));
    setError("");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Swords className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              {missao ? "Editar missão" : "Nova missão"}
            </CardTitle>
            <CardDescription>Objetivos medidos por variáveis do jogo</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0" disabled={submitting}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Conexão</Label>
              <Select value={idConexao} onValueChange={setIdConexao} disabled={submitting}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma conexão" />
                </SelectTrigger>
                <SelectContent>
                  {conexoes.map((conexao) => (
                    <SelectItem key={conexao.id} value={conexao.id}>
                      {conexao.nome} (quarto {conexao.room_id}){!conexao.ativo ? " - inativa" : ""}
                      {!conexao.possui_chave_escrita ? " - sem chave de escrita" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {semChave && recompensas.length > 0 && (
                <p className="text-xs text-amber-600">Esta conexão não tem chave de escrita: as recompensas não poderão ser pagas.</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="missao_nome">Nome</Label>
              <Input
                id="missao_nome"
                value={nome}
                onChange={(e) => {
                  setNome(e.target.value);
                  setError("");
                }}
                placeholder="Ex: Caçador de zumbis"
                disabled={submitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="missao_descricao">Descrição</Label>
              <textarea
                id="missao_descricao"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                rows={3}
                maxLength={1000}
                disabled={submitting}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="missao_imagem">Link da imagem (opcional)</Label>
              <Input
                id="missao_imagem"
                value={imagem}
                onChange={(e) => {
                  setImagem(e.target.value);
                  setImagemQuebrada(false);
                  setError("");
                }}
                placeholder="https://..."
                disabled={submitting}
              />
              {imagem.trim() && !imagemQuebrada && (
                <img
                  src={imagem.trim()}
                  alt="Prévia"
                  onError={() => setImagemQuebrada(true)}
                  className="h-24 rounded-md border border-border object-cover"
                />
              )}
              {imagemQuebrada && <p className="text-xs text-amber-600">Não foi possível carregar a imagem.</p>}
            </div>

            <div className="space-y-3 rounded-lg border border-border p-4">
              <Label>Prazo e repetição</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Prazo</Label>
                  <Select
                    value={prazoTipo}
                    onValueChange={(v) => {
                      setPrazoTipo(v as WiredMissaoPrazoTipo);
                      if (v === "fixo") setRepetivel(false);
                    }}
                    disabled={submitting}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nenhum">Sem prazo</SelectItem>
                      <SelectItem value="relativo">Tempo a partir do aceite</SelectItem>
                      <SelectItem value="fixo">Data fixa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {prazoTipo === "relativo" && (
                  <div className="space-y-1">
                    <Label className="text-xs">Duração</Label>
                    <div className="flex gap-2">
                      <Input
                        value={prazoValor}
                        onChange={(e) => setPrazoValor(e.target.value)}
                        inputMode="numeric"
                        className="font-mono"
                        disabled={submitting}
                      />
                      <Select value={prazoUnidade} onValueChange={(v) => setPrazoUnidade(v as "horas" | "dias")} disabled={submitting}>
                        <SelectTrigger className="w-28">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="horas">horas</SelectItem>
                          <SelectItem value="dias">dias</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                {prazoTipo === "fixo" && (
                  <div className="space-y-1">
                    <Label className="text-xs">Data limite</Label>
                    <Input type="datetime-local" value={prazoAte} onChange={(e) => setPrazoAte(e.target.value)} disabled={submitting} />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-center justify-between rounded-lg border border-border p-3">
                  <Label htmlFor="missao_repetivel" className="text-sm">
                    Repetível
                  </Label>
                  <Switch
                    id="missao_repetivel"
                    checked={repetivel}
                    onCheckedChange={setRepetivel}
                    disabled={submitting || prazoTipo === "fixo"}
                  />
                </div>
                {repetivel && (
                  <div className="space-y-1">
                    <Label className="text-xs">Intervalo para repetir (horas)</Label>
                    <Input
                      value={cooldown}
                      onChange={(e) => setCooldown(e.target.value)}
                      inputMode="numeric"
                      placeholder="Sem intervalo"
                      className="font-mono"
                      disabled={submitting}
                    />
                  </div>
                )}
              </div>
              {prazoTipo === "fixo" && (
                <p className="text-xs text-muted-foreground">Missões com data fixa não podem ser repetíveis.</p>
              )}
            </div>

            <div className="space-y-3 rounded-lg border border-border p-4">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <Label>Objetivos</Label>
                  <p className="text-xs text-muted-foreground">
                    "A partir do aceite" conta só o que o militar fizer depois de aceitar. "Retroativo" usa o valor total atual da variável.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={submitting || objetivos.length >= MAX_OBJETIVOS}
                  onClick={() => setObjetivos((a) => [...a, { rotulo: "", nome_variavel: "", meta: "1", modo: "delta" }])}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </div>
              {objetivos.map((o, i) => (
                <div key={i} className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
                  <div className="space-y-1 sm:col-span-2">
                    <Label className="text-xs">Rótulo</Label>
                    <Input
                      value={o.rotulo}
                      onChange={(e) => alterarObjetivo(i, { rotulo: e.target.value })}
                      placeholder="Ex: Eliminar inimigos"
                      disabled={submitting}
                    />
                  </div>
                  <div className="space-y-1 sm:col-span-2">
                    <Label className="text-xs">Conexão</Label>
                    <Select
                      value={o.id_conexao ?? idConexao}
                      onValueChange={(v) =>
                        alterarObjetivo(i, { id_conexao: v === idConexao ? null : v, nome_variavel: "" })
                      }
                      disabled={submitting}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {conexoes.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1 sm:col-span-2">
                    <Label className="text-xs">Variável</Label>
                    <VariavelWiredSelect
                      value={o.nome_variavel}
                      onChange={(v) => alterarObjetivo(i, { nome_variavel: v })}
                      idConexao={o.id_conexao ?? idConexao}
                      ativo={isOpen}
                      disabled={submitting}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Meta</Label>
                    <Input
                      value={o.meta}
                      onChange={(e) => alterarObjetivo(i, { meta: e.target.value })}
                      inputMode="numeric"
                      className="font-mono"
                      disabled={submitting}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Contagem</Label>
                    <Select
                      value={o.modo}
                      onValueChange={(v) => alterarObjetivo(i, { modo: v as WiredMissaoObjetivo["modo"] })}
                      disabled={submitting}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="delta">A partir do aceite</SelectItem>
                        <SelectItem value="retroativo">Retroativo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-red-600"
                    disabled={submitting}
                    onClick={() => setObjetivos((a) => a.filter((_, j) => j !== i))}
                    aria-label="Remover objetivo"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <div className="col-span-full flex items-center gap-2">
                    <Switch
                      id={`obj_obrigatorio_${i}`}
                      checked={!!o.obrigatorio}
                      onCheckedChange={(v) => alterarObjetivo(i, { obrigatorio: v })}
                      disabled={submitting}
                    />
                    <Label htmlFor={`obj_obrigatorio_${i}`} className="text-xs">
                      Obrigatório (sempre entra na missão, fora do sorteio)
                    </Label>
                  </div>
                </div>
              ))}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Sortear quantos objetivos? (além dos obrigatórios)</Label>
                  <Input
                    value={sorteio}
                    onChange={(e) => setSorteio(e.target.value)}
                    inputMode="numeric"
                    placeholder={`Todos (${objetivos.length})`}
                    className="font-mono"
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3 rounded-lg border border-border p-4">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <Label>Requisitos para aceitar</Label>
                  <p className="text-xs text-muted-foreground">Variável ausente conta como 0.</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={submitting || requisitos.length >= MAX_ITENS}
                  onClick={() => setRequisitos((a) => [...a, { rotulo: "", nome_variavel: "", operador: ">=", valor: "1" }])}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </div>
              {requisitos.map((r, i) => (
                <div key={i} className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
                  <div className="space-y-1 sm:col-span-2">
                    <Label className="text-xs">Rótulo</Label>
                    <Input value={r.rotulo} onChange={(e) => alterarRequisito(i, { rotulo: e.target.value })} disabled={submitting} />
                  </div>
                  <div className="space-y-1 sm:col-span-2">
                    <Label className="text-xs">Variável</Label>
                    <VariavelWiredSelect
                      value={r.nome_variavel}
                      onChange={(v) => alterarRequisito(i, { nome_variavel: v })}
                      idConexao={idConexao}
                      ativo={isOpen}
                      disabled={submitting}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Condição</Label>
                    <Select
                      value={r.operador}
                      onValueChange={(v) => alterarRequisito(i, { operador: v as WiredLojaRequisito["operador"] })}
                      disabled={submitting}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {WIRED_LOJA_OPERADORES.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Valor</Label>
                    <Input
                      value={r.valor}
                      onChange={(e) => alterarRequisito(i, { valor: e.target.value })}
                      inputMode="numeric"
                      className="font-mono"
                      disabled={submitting}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-red-600"
                    disabled={submitting}
                    onClick={() => setRequisitos((a) => a.filter((_, j) => j !== i))}
                    aria-label="Remover requisito"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  {niveisXp.includes(r.nome_variavel.trim()) && (
                    <div className="col-span-full flex items-center gap-2 text-xs text-muted-foreground">
                      <span>Esta variável tem sistema de nível. Comparar por:</span>
                      <Select
                        value={r.tipo === "nivel" ? "nivel" : "variavel"}
                        onValueChange={(v) => alterarRequisito(i, { tipo: v as "nivel" | "variavel" })}
                        disabled={submitting}
                      >
                        <SelectTrigger className="h-8 w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="nivel">Nível</SelectItem>
                          <SelectItem value="variavel">XP</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="space-y-3 rounded-lg border border-border p-4">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <Label>Recompensas</Label>
                  <p className="text-xs text-muted-foreground">Somadas à variável quando o militar concluir (cria a variável se não existir).</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={submitting || recompensas.length >= MAX_ITENS}
                  onClick={() => setRecompensas((a) => [...a, { rotulo: "", nome_variavel: "", valor: "1", teto_valor: null }])}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </div>
              {recompensas.map((r, i) => (
                <div key={i} className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
                  <div className="space-y-1 sm:col-span-2">
                    <Label className="text-xs">Rótulo</Label>
                    <Input value={r.rotulo} onChange={(e) => alterarRecompensa(i, { rotulo: e.target.value })} disabled={submitting} />
                  </div>
                  <div className="space-y-1 sm:col-span-2">
                    <Label className="text-xs">Conexão</Label>
                    <Select
                      value={r.id_conexao ?? idConexao}
                      onValueChange={(v) => alterarRecompensa(i, { id_conexao: v === idConexao ? null : v })}
                      disabled={submitting}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {conexoes.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.nome}
                            {!c.possui_chave_escrita ? " - sem chave de escrita" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1 sm:col-span-2">
                    <Label className="text-xs">Variável</Label>
                    <VariavelWiredSelect
                      value={r.nome_variavel}
                      onChange={(v) => alterarRecompensa(i, { nome_variavel: v })}
                      idConexao={r.id_conexao ?? idConexao}
                      ativo={isOpen}
                      disabled={submitting}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Quantidade</Label>
                    <Input
                      value={r.valor}
                      onChange={(e) => alterarRecompensa(i, { valor: e.target.value })}
                      inputMode="numeric"
                      className="font-mono"
                      disabled={submitting}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Teto</Label>
                    <Input
                      value={r.teto_valor ?? ""}
                      onChange={(e) => alterarRecompensa(i, { teto_valor: e.target.value })}
                      inputMode="numeric"
                      placeholder="Opcional"
                      className="font-mono"
                      disabled={submitting}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-red-600"
                    disabled={submitting}
                    onClick={() => setRecompensas((a) => a.filter((_, j) => j !== i))}
                    aria-label="Remover recompensa"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="missao_limite">Limite simultâneo</Label>
                <Input
                  id="missao_limite"
                  value={limite}
                  onChange={(e) => setLimite(e.target.value)}
                  inputMode="numeric"
                  placeholder="Usar o global"
                  className="font-mono"
                  disabled={submitting}
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border p-3 self-end">
                <Label htmlFor="missao_ativo">Ativa</Label>
                <Switch id="missao_ativo" checked={ativo} onCheckedChange={setAtivo} disabled={submitting} />
              </div>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={submitting}>
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : missao ? (
                  "Salvar"
                ) : (
                  "Criar"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
